import { mkNode, removeNode } from "./utils";
import { Img } from './image-base';
import { Json, LocalData } from './exam-service';
import { ThumbnailViewer } from "thumbnail-viewer";
import { Lightbox } from "lightbox";
import { translate } from "utils-lang";

export interface AnswerFactory {
    name: string;
    isThis(answer: PractiqueNet.ExamJson.Definitions.Answer, n?: number): boolean;
    makeAnswer(
        pos: number,
        context: QuestionContext,
        updateVisibility: () => void,
        question: QuestionManifest,
        answer: PractiqueNet.ExamJson.Definitions.Answer,
        frag: DocumentFragment,
        ano: number,
        lightbox: Lightbox
    ): Question;
}

export const answerTypes: AnswerFactory[] = [];

export function registerAnswerType(af: AnswerFactory): void {
    console.info('REGISTER_QUESTION', af.name)
    answerTypes.push(af);
}

/** Expression dependent on answer values */
export type ExprVal = PractiqueNet.ExamJson.Definitions.ExprVal;
export type ExprRef = PractiqueNet.ExamJson.Definitions.ExprRef;
export type ExprObject = PractiqueNet.ExamJson.Definitions.ExprObject;
export type ExprEq = PractiqueNet.ExamJson.Definitions.ExprEq;
export type ExprGt = PractiqueNet.ExamJson.Definitions.ExprGt;
export type ExprLt = PractiqueNet.ExamJson.Definitions.ExprLt;
export type ExprGe = PractiqueNet.ExamJson.Definitions.ExprGe;
export type ExprLe = PractiqueNet.ExamJson.Definitions.ExprLe;
export type ExprAnd = PractiqueNet.ExamJson.Definitions.ExprAnd;
export type ExprOr = PractiqueNet.ExamJson.Definitions.ExprOr;
export type ExprXor = PractiqueNet.ExamJson.Definitions.ExprXor;
export type ExprNot = PractiqueNet.ExamJson.Definitions.ExprNot;
export type Expr = PractiqueNet.ExamJson.Definitions.Expr;


/** Identify question independent of local question order in exam */
export interface BackendId {
    backendQid: number;
    backendAid: number;
}

/** Data to save in local DB and send to server */
export interface SaveArgs {
    qno: number;
    ano: number;
    backendQid: number;
    backendAid: number;
    time: number;
    answer: string;
}

export interface ControlPanel {
    add(control: HTMLElement): boolean;
    remove(control: HTMLElement): boolean;
    panel(): HTMLElement;
}

export interface NotificationArea {
    show(html: string): void;
    hide(): void;
    setReadOnly(isReadOnly: boolean): void;
}

/** Injects dependencies for questions */
export interface QuestionContext {
    controlPanel: ControlPanel;
    getNavigating(): boolean;
    setNavigating(x: boolean): void;
    setFlag(qid: number, aid: number, flag: boolean): Promise<void>;
    getFlag(qid: number, aid: number): Promise<boolean>;
    loadAnswer(qno: number, ano: number): Promise<LocalData|undefined>;
    saveAnswer(qno: number, ano: number, time: number, answer: Json, extra?: Json): Promise<void>;
    setVisible(qid: number, aid: number, vis: boolean): void;
    getValid(): boolean;
    setValid(valid: boolean): void;
    getImageBegin(): Promise<void>;
    getImageFrame(image: Img, frame: number): Promise<ArrayBuffer>;
    getImageEnd(): Promise<void>;
    getDisplayId(qno: number, ano: number): string|undefined;
    parent: HTMLElement;
    fullscreenParent: HTMLElement;
    //noMouse: boolean;
    //showQuestionTitle: boolean;
    factorDetails?: {[ix: string]: PractiqueNet.ExamJson.Definitions.UserDetails};
    candidateCid: string;
    //examId: string;
    meetingBar: HTMLElement;
    //enableCopyPaste: boolean;
    //disableResourceLocking: boolean;
    meta: PractiqueNet.ExamJson.Definitions.ExamMeta;
    notificationArea: HTMLDivElement;
}

export interface AnswerResources {
    thumbnails: (ArrayBuffer|null)[];
    resources: Img[];
}

export interface QuestionManifest {
    manifest: PractiqueNet.ExamJson.Definitions.Question;
    images: Img[];
    thumbnails: (ArrayBuffer|null)[];
    answersResources: AnswerResources[];
}

export class QuestionManifest implements QuestionManifest {
    public constructor(manifest: PractiqueNet.ExamJson.Definitions.Question) {
        this.manifest = manifest;
        this.images = [];
        this.thumbnails = [];
    }
}

export interface Question {
    loadFlag(): Promise<void>;
    loadAnswer(): Promise<void>;
    loadResources(thumbnails: (ArrayBuffer|null)[]): Promise<void>
    loadingComplete(): void;
    getValue(): string;
    setVisible(x: boolean): void;
    disableResources(x: boolean): void;
    setReadOnly(x: boolean): void;
    destroy(): void;
    focus(): void;
    visibilityExpression?: Expr;  
}

export enum Layout {
    Default,
    Compact,
    CompactFloat,
}

/** Base class for questions, renders stem and flag UI */
export class QuestionBase {
    protected readonly context: QuestionContext;
    protected readonly startTime: number;

    protected readonly label: HTMLParagraphElement;
    protected readonly labelId: HTMLDivElement;
    protected readonly column: HTMLDivElement;
    protected readonly labelPanel: HTMLDivElement;
    protected readonly id: Text;
    //protected readonly text: HTMLSpanElement;
    //protected readonly notes?: HTMLDivElement;
    protected readonly flag: HTMLButtonElement;
    //protected readonly thumbs?: HTMLDivElement;
    //protected readonly ftxt: Text;
    protected readonly thumbnails?: ThumbnailViewer;

    protected isLoading = false;
    protected isReadOnly = false;

    public readonly qno: number;
    public readonly ano: number;
    public readonly backendQid: number; 
    public readonly backendAid: number;
    /** Constructs the QuestionBase UI */
    public constructor(
        context: QuestionContext,
        frag: Node,
        qno: number,
        ano: number, 
        backendQid: number, 
        backendAid: number, 
        showNumber: boolean, 
        showFlag: boolean,
        label: string,
        lightbox: Lightbox,
        notes?: string,
        resources?: AnswerResources,
        indent?: number,
        layout = Layout.Default,
    ) {
        this.context = context;
        this.qno = qno;
        this.ano = ano;
        this.backendQid = backendQid;
        this.backendAid = backendAid;
        this.startTime = Date.now();

        const displayId = context.getDisplayId(this.qno, this.ano);
        this.label = mkNode('div', { className: `answer-label-row ${(this.ano % 2) ? 'config-background-highlight-faint' : 'config-background'}` });
        this.labelId = mkNode('div', { className: 'answer-label-id', parent: this.label });
        
        if (resources && ((resources.thumbnails?.length ?? 0) > 0)) {
            layout = Layout.Default;
        }

        if (layout === Layout.Compact) {
            this.labelPanel = this.label;
            this.column = this.label;
            this.flag = mkNode('button', {
                parent: (showFlag ? this.label : undefined), title: translate('ANSWER_FLAG_TIP'), tabindex: 0, disabled: true,
                children: [
                    mkNode('text', { text: '\u2691' })
                ]
            });
            this.flag.style.order = '999';
            this.flag.style.marginRight = '16px';
        } else if (layout === Layout.CompactFloat) {
            this.labelPanel = mkNode('div', {className: 'answer-label-panel', parent: this.label});
            this.flag = mkNode('button', {
                parent: (showFlag ? this.labelPanel : undefined), title: translate('ANSWER_FLAG_TIP'), tabindex: 0, disabled: true,
                children: [
                    mkNode('text', { text: '\u2691' })
                ]
            });
            this.column = mkNode('div', { className: 'answer-label-col-compact', parent: this.labelPanel });
       } else {
            this.column = mkNode('div', { className: 'answer-label-col', parent: this.label });
            this.labelPanel =  mkNode('div', {className: 'answer-label-panel', parent: this.column});
            this.flag = mkNode('button', {
                className:'flag', parent: (showFlag ? this.labelPanel : undefined), title: translate('ANSWER_FLAG_TIP'), tabindex: 0, disabled: true,
                children: [
                    mkNode('text', { text: '\u2691' })
                ]
            });
        }

        this.id = mkNode('text', { parent: this.labelId });

        const indentRem = (1.6 * (indent ?? 0) + 1.6).toString();
        this.label.style.paddingLeft = `${indentRem}rem`; 
        
        let text;
        if (label) {
            text = mkNode('span', { className: 'answer-label-text', parent: this.labelPanel });
            text.innerHTML = label;
        }
        
        if (notes) {
            const notesElement = mkNode('div', { className: 'answer-label-notes', parent: this.labelPanel });
            notesElement.innerHTML = notes;
        }

        if (resources && ((resources.thumbnails?.length ?? 0) > 0)) {
            this.thumbnails = new ThumbnailViewer({
                fullscreenParent: this.context.fullscreenParent,
                scrollContainer: this.context.parent,
                sizeReference: this.context.parent,
                getImageBegin: this.context.getImageBegin,
                getImageFrame: this.context.getImageFrame,
                getImageEnd: this.context.getImageEnd,
                getNavigating: this.context.getNavigating,
                setNavigating: this.context.setNavigating,
                //noMouse: this.context.noMouse,
            }, this.column, resources.resources, lightbox);
        }

        if (showNumber && displayId) {
            this.id.textContent = displayId;
        } else {
            this.labelId.style.display = 'none';
        }

        frag.appendChild(this.label);
        this.isLoading = true;
    }

    public updateDisable(): void {
        this.flag.disabled = this.isDisabled();
    }

    public async loadResources(thumbnails: (ArrayBuffer|null)[]): Promise<void> {
        if (this.thumbnails) {
            await this.thumbnails.loadResources(thumbnails);
        }
    }

    /** Loads the QuestionBase state */
    public async loadFlag(): Promise<void> {
        try {
            this.setFlag(await this.context.getFlag(this.qno, this.ano));
        } catch (e) {
            console.error(e);
        } finally {
            this.flag.addEventListener('click', this.flagClickHandler);
        }
    }

    public loadingComplete(): void {
        this.isLoading = false;
        this.updateDisable();
    }

    public disableResources(disable: boolean): void {
        this.thumbnails?.disabled(disable);
    }

    /** Frees resources used by QuestionBase */
    public destroy(): void {
        removeNode(this.label);
        if (this.thumbnails) {
            this.thumbnails.destroy();
        }
        this.flag.removeEventListener('click', this.flagClickHandler);
    }

    public setReadOnly(isReadOnly: boolean): void {
        this.isReadOnly = isReadOnly;
        this.updateDisable();
    }

    public isDisabled(): boolean {
        return this.isLoading || this.isReadOnly;
    }

    private setFlag(x: boolean): void {
        if (x) {
            this.flag.setAttribute('aria-pressed', 'true');
            this.flag.className = 'flag config-primary config-primary-hover config-primary-border config-body-border-focus config-primary-shadow-empty-focus';
        } else {
            this.flag.setAttribute('aria-pressed', 'false');
            this.flag.className = 'flag config-background-text config-background-hover config-primary-border-empty config-primary-shadow-empty-focus';
        }
    }

    private readonly flagClickHandler = async (): Promise<void> => {
        if (this.context.getNavigating()) {
            return;
        }

        this.context.setNavigating(true);
        try {
            const notFlag = this.flag.getAttribute('aria-pressed') !== 'true';
            await this.context.setFlag(this.qno, this.ano, notFlag);
            this.setFlag(notFlag);
        } catch (e) {
            console.error(e);
        } finally {
            this.context.setNavigating(false);
        }
    }
}
